import { useState } from "react";
import { Telescope, Bookmark, Share2, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShareDialog } from "@/components/ShareDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { RENTAL } from "@/constants/testIds";
import { LOGOUT } from "@/constants/testIds/auth";

export const Header = ({ stats, onOpenBookmarklet }) => {
  const { user, logout } = useAuth();
  const [shareOpen, setShareOpen] = useState(false);
  const avgRent =
    stats?.avg_rent != null ? `$${Math.round(stats.avg_rent).toLocaleString()}` : "—";

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F8F6]/75 border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid={RENTAL.appLogo}>
          <div className="w-10 h-10 rounded-xl bg-[#4A5D4E] flex items-center justify-center text-[#F9F8F6] -rotate-6">
            <Telescope size={20} strokeWidth={1.6} />
          </div>
          <div className="leading-none">
            <div className="font-heading text-2xl font-medium tracking-tight">
              LeaseDeck
            </div>
            <div className="overline mt-1">Your Rental Shortlist</div>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-8">
          <Stat label="Listings" value={stats?.total ?? 0} testid={RENTAL.headerStatsTotal} />
          <div className="h-8 w-px bg-stone-200" />
          <Stat
            label="Favorites"
            value={stats?.favorites ?? 0}
            testid={RENTAL.headerStatsFavs}
            accent
          />
          <div className="h-8 w-px bg-stone-200" />
          <Stat label="Avg Rent" value={avgRent} testid={RENTAL.headerStatsAvg} />
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenBookmarklet}
            data-testid={RENTAL.bookmarkletOpenBtn}
            className="inline-flex items-center gap-1.5 rounded-full border-stone-300 bg-white/70 hover:bg-white text-stone-700"
          >
            <Bookmark size={14} strokeWidth={1.8} />
            <span className="hidden sm:inline">Bookmarklet</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border-stone-300 bg-white/70 hover:bg-white text-stone-700"
          >
            <Share2 size={14} strokeWidth={1.8} />
            <span className="hidden sm:inline">Share</span>
          </Button>

          <ShareDialog open={shareOpen} onOpenChange={setShareOpen} />

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="rounded-full outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-[#4A5D4E]"
                  aria-label="Account menu"
                >
                  <Avatar className="h-9 w-9 border border-stone-200">
                    {user.picture ? (
                      <AvatarImage src={user.picture} alt={user.name || user.email} />
                    ) : null}
                    <AvatarFallback className="bg-[#4A5D4E] text-[#F9F8F6] text-sm">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="font-medium truncate">{user.name || "Account"}</span>
                  <span className="text-xs text-stone-500 truncate">{user.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  data-testid={LOGOUT.button}
                  className="text-red-600 focus:text-red-600"
                >
                  <LogOut size={15} strokeWidth={1.8} className="mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};

const Stat = ({ label, value, testid, accent }) => (
  <div className="flex flex-col" data-testid={testid}>
    <span className="overline">{label}</span>
    <span
      className={`font-heading text-xl font-medium mt-0.5 leading-none ${
        accent ? "text-[#C25934]" : "text-stone-900"
      }`}
    >
      {value}
    </span>
  </div>
);

export default Header;
